import React, { useState, useEffect, useRef } from 'react';
import * as api from './api';
import { track } from './track';
import Share from './Share';
import './App.css';

const SANCTION_OPTIONS = [
  { value: 'caution', label: 'They could receive a formal caution or warning on their record, without restricting their practice' },
  { value: 'conditions', label: 'They could be allowed to continue working, but with monitoring and restrictions in place for a period of time' },
  { value: 'suspension', label: 'They should be temporarily prevented from practising' },
  { value: 'struck_off', label: 'They should be permanently removed from the register and no longer allowed to practise' },
  { value: 'no_sanction', label: 'The process itself is sufficient \u2014 no sanction is needed' },
];

const OUTCOME_ACHIEVES = [
  'It protects the public from further harm',
  'It acknowledges the seriousness of what happened',
  'It maintains the public\u2019s trust in the profession as a whole',
  'It would give me greater confidence when approaching other professionals in the same field',
  'It feels like a fair and proportionate response',
  'It provides justice for anyone affected',
  'It sends a clear message about the standards expected in that profession',
  'It holds the person accountable for what they have done',
];

const RESPONDENT_TYPES = [
  { value: 'health_care', label: 'I work in the health or care sector' },
  { value: 'legal', label: 'I work in the legal sector' },
  { value: 'other_sector', label: 'I work in another sector' },
  { value: 'student', label: 'I am a student' },
  { value: 'retired', label: 'I am retired' },
];

const SANCTION_LABELS: Record<string, string> = {
  caution: 'Caution/warning', conditions: 'Conditions', suspension: 'Suspension',
  struck_off: 'Permanent removal', no_sanction: 'No sanction',
};

function App() {
  const [phase, setPhase] = useState<'landing' | 'survey' | 'payoff' | 'exhausted' | 'error'>('landing');
  const [scenario, setScenario] = useState<any>(null);
  const [factors, setFactors] = useState<{id: string; text: string}[]>([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    q1a: '', q1b: '', q2a: '', q2c: '', q2d: '',
    q3a: '', q3b: [] as string[],
    factor1Changed: '', q3aAfterFactor1: '',
    factor2Changed: '', q3aAfterFactor2: '',
    respondentType: '',
  });
  const [payoff, setPayoff] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // ⚠️ THE SERVER ISSUES THIS, per scenario. A browser-generated token proves nothing —
  // it is the first thing a script would invent — and the elapsed time that tells a reader
  // from a bot is only measurable between two writes we made ourselves. Falls back to a
  // local id so a respondent is never blocked if issuing fails; they are simply untrusted.
  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { api.getStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
  }, [step]);

  function set(field: string, value: any) {
    setAnswers(a => ({ ...a, [field]: value }));
  }

  async function startSurvey() {
    setLoading(true);
    try {
      const [scenarioRes, factorsRes] = await Promise.all([
        api.getScenario(completedIds),
        api.getFactors(),
      ]);
      if (scenarioRes && scenarioRes.scenario) {
        if (scenarioRes.sessionToken) setSessionToken(scenarioRes.sessionToken);
        setScenario(scenarioRes.scenario);
        setFactors(factorsRes.factors || []);
        setAnswers({ q1a: '', q1b: '', q2a: '', q2c: '', q2d: '', q3a: '', q3b: [], factor1Changed: '', q3aAfterFactor1: '', factor2Changed: '', q3aAfterFactor2: '', respondentType: '' });
        setPayoff(null); setStep(1); setPhase('survey');
        track('research_start', { scenario_id: scenarioRes.scenario.id, scenario_number: completedIds.length + 1 });
      } else {
        // No scenario came back — either the respondent has answered them all
        // this session, or none are currently active. Never leave the button dead.
        window.scrollTo(0, 0);
        track('research_exhausted', { completed: completedIds.length });
        setPhase('exhausted');
      }
    } catch (e) {
      console.error(e);
      track('research_error', { where: 'start' });
      window.scrollTo(0, 0);
      setPhase('error');
    } finally {
      setLoading(false);
    }
  }

  async function submit() {
    setLoading(true);
    try {
      const res = await api.submitResponseV2({
        scenarioId: scenario.id, sessionToken, ...answers,
        factor1Id: factors[0]?.id || null,
        factor2Id: factors[1]?.id || null,
      });
      setPayoff(res.payoff);
      track('research_complete', {
        scenario_id: scenario.id,
        answer: answers.q3a,
        moved: answers.factor1Changed === 'Yes' || answers.factor2Changed === 'Yes',
        respondent_type: answers.respondentType,
      });
      setCompletedIds([...completedIds, scenario.id]);
      api.getStats().then(setStats).catch(() => {});
      // Step is set by the caller before calling submit — don't advance here
    } catch (e) { console.error(e); track('research_error', { where: 'submit' }); }
    setLoading(false);
  }

  function advance() {
    setStep(s => { track('research_step', { step: s + 1, scenario_id: scenario?.id }); return s + 1; });
  }

  function YesNo({ q, field }: { q: string; field: string }) {
    const val = (answers as any)[field];
    return (
      <div className="q-block">
        <p className="q-text">{q}</p>
        <div className="yesno-row">
          {['Yes', 'No', 'Not sure'].map(o => (
            <button key={o} className={`yesno-btn ${val === o ? 'selected' : ''}`} onClick={() => set(field, o)}>{o}</button>
          ))}
        </div>
      </div>
    );
  }

  function SanctionChoice({ field }: { field: string }) {
    const val = (answers as any)[field];
    return (
      <div className="q-block">
        <p className="q-text">What do you think is the most appropriate outcome?</p>
        <div className="option-list">
          {SANCTION_OPTIONS.map(o => (
            <button key={o.value} className={`option-btn ${val === o.value ? 'selected' : ''}`} onClick={() => set(field, o.value)}>{o.label}</button>
          ))}
        </div>
      </div>
    );
  }

  // Steps: 1=scenario, 2=q1a+q1b, 3=bridge+q2a-d, 4=panel+q3a, 5=q3b,
  // 6=factor1+q4, 7=q3a repeat if changed, 8=factor2+q5, 9=q3a repeat if changed,
  // 10=payoff, 11=demographics, 12=email
  const hasFactor2 = factors.length >= 2;

  return (
    <div className="survey-app">
      <header className="survey-header">
        <div className="header-brand">
          <img src="/logo.png" alt="Sequentus" className="header-logo" />
          <span className="header-sub">Research</span>
        </div>
        {stats && <FundMeter stats={stats} />}
      </header>

      <main className="survey-main">
        {loading && <div className="loading-overlay">Loading...</div>}

        {phase === 'landing' && (
          <div className="survey-card landing">
            <h2>What would you do?</h2>
            <p>We're conducting research into how members of the public view cases where a health, care, legal or other professional faces disciplinary proceedings by the organisation that regulates their work. It takes about 3 minutes.</p>
            <p>You'll read a short scenario about a professional whose case is going to be heard by an independent panel, then tell us what you think should happen. This is not about employment &mdash; it is about the right to practise in a regulated profession.</p>
            <p className="highlight">For every completed response we set aside 50p in our access to justice fund &mdash; up to &pound;2,000 in this first phase &mdash; which supports registrants going through fitness to practise proceedings without representation.</p>
            <button className="btn-primary large" onClick={startSurvey}>Start</button>
            <p className="landing-about"><a href="https://education.sequentus.org/research/">Who is asking, and why</a> &mdash; about the study, where the scenarios come from, and what happens to your answers.</p>
          </div>
        )}

        {phase === 'exhausted' && (
          <div className="survey-card landing">
            <h2>{completedIds.length ? 'That\u2019s all of them for now' : 'Nothing available just now'}</h2>
            {completedIds.length ? (
              <>
                <p>You have answered every scenario currently live on the study — {completedIds.length} {completedIds.length === 1 ? 'case' : 'cases'}. Thank you.</p>
                <p>We add new cases as regulators publish their decisions, so there will be more here before long. Do come back.</p>
              </>
            ) : (
              <p>There are no scenarios available at the moment. Please check back shortly.</p>
            )}
          </div>
        )}

        {phase === 'error' && (
          <div className="survey-card landing">
            <h2>Something went wrong</h2>
            <p>We could not load a scenario just then. This is our end, not yours.</p>
            <button className="btn-primary" onClick={() => { setPhase('landing'); startSurvey(); }}>Try again</button>
          </div>
        )}

        {phase === 'survey' && (
          <div className="progressive-flow">

            {/* 1: Scenario */}
            {step >= 1 && (
              <div className="survey-card"><div className="scenario-label">Scenario</div>
                <p className="scenario-text">{scenario?.scenarioText}</p>
                {step === 1 && <button className="btn-primary" onClick={advance}>Continue</button>}
              </div>
            )}

            {/* 2: Q1a + Q1b */}
            {step >= 2 && (
              <div className="survey-card fade-in">
                <YesNo q="Do you think the regulator should take action based on what you have read?" field="q1a" />
                <YesNo q="Would you be concerned if this person was allowed to continue practising without further action?" field="q1b" />
                {step === 2 && answers.q1a && answers.q1b && <button className="btn-primary" onClick={advance}>Continue</button>}
              </div>
            )}

            {/* 3: Bridge + Q2a-d */}
            {step >= 3 && (
              <div className="survey-card fade-in">
                <div className="bridge-text">The regulator investigated. At a hearing, an independent panel found the facts of what happened proven.</div>
                <YesNo q="Do you think what happened is so serious that further action should be taken?" field="q2a" />
                <YesNo q="Do you think what has been described reflects badly on the profession as a whole?" field="q2c" />
                <YesNo q="Based on what you have read, would you have any hesitation about using the services of another professional in the same field?" field="q2d" />
                {step === 3 && answers.q2a && answers.q2c && answers.q2d && <button className="btn-primary" onClick={advance}>Continue</button>}
              </div>
            )}

            {/* 4: Panel instruction + Q3a */}
            {step >= 4 && (
              <div className="survey-card fade-in">
                <div className="bridge-text">The independent panel hearing this case decided that a sanction should be considered.</div>
                <SanctionChoice field="q3a" />
                {step === 4 && answers.q3a && <button className="btn-primary" onClick={advance}>Continue</button>}
              </div>
            )}

            {/* 5: Q3b — what does outcome achieve */}
            {step >= 5 && (
              <div className="survey-card fade-in">
                <div className="q-block">
                  <p className="q-text">What do you think the outcome you chose achieves?</p>
                  <div className="option-list">
                    {OUTCOME_ACHIEVES.map(o => (
                      <button key={o} className={`option-btn multi ${answers.q3b.includes(o) ? 'selected' : ''}`}
                        onClick={() => set('q3b', answers.q3b.includes(o) ? answers.q3b.filter((v: string) => v !== o) : [...answers.q3b, o])}>{o}</button>
                    ))}
                  </div>
                </div>
                {step === 5 && answers.q3b.length > 0 && <button className="btn-primary" onClick={advance}>Continue</button>}
              </div>
            )}

            {/* 6: Factor reveal 1 + Q4 */}
            {step >= 6 && factors[0] && (
              <div className="survey-card fade-in">
                <div className="factor-label">New information</div>
                <p className="factor-text">{factors[0].text}</p>
                <YesNo q="Does this change your view on the appropriate outcome?" field="factor1Changed" />
                {step === 6 && answers.factor1Changed && (
                  answers.factor1Changed === 'Yes'
                    ? <button className="btn-primary" onClick={advance}>Choose new outcome</button>
                    : hasFactor2
                      ? <button className="btn-primary" onClick={() => setStep(8)}>Continue</button>
                      : <button className="btn-primary" onClick={() => { setStep(10); submit(); }}>Continue</button>
                )}
              </div>
            )}

            {/* 7: Q3a repeat after factor 1 */}
            {step >= 7 && answers.factor1Changed === 'Yes' && (
              <div className="survey-card fade-in">
                <SanctionChoice field="q3aAfterFactor1" />
                {step === 7 && answers.q3aAfterFactor1 && (
                  hasFactor2
                    ? <button className="btn-primary" onClick={() => setStep(8)}>Continue</button>
                    : <button className="btn-primary" onClick={() => { setStep(10); submit(); }}>Continue</button>
                )}
              </div>
            )}

            {/* 8: Factor reveal 2 + Q5 */}
            {step >= 8 && hasFactor2 && (
              <div className="survey-card fade-in">
                <div className="factor-label">Further information</div>
                <p className="factor-text">{factors[1].text}</p>
                <YesNo q="Does this change your view on the appropriate outcome?" field="factor2Changed" />
                {step === 8 && answers.factor2Changed && (
                  answers.factor2Changed === 'Yes'
                    ? <button className="btn-primary" onClick={advance}>Choose new outcome</button>
                    : <button className="btn-primary" onClick={() => { setStep(10); submit(); }}>Continue</button>
                )}
              </div>
            )}

            {/* 9: Q3a repeat after factor 2 */}
            {step >= 9 && answers.factor2Changed === 'Yes' && (
              <div className="survey-card fade-in">
                <SanctionChoice field="q3aAfterFactor2" />
                {step === 9 && answers.q3aAfterFactor2 && (
                  <button className="btn-primary" onClick={() => { setStep(10); submit(); }}>Continue</button>
                )}
              </div>
            )}

            {/* 10: Demographics (before payoff to ensure completion) */}
            {step >= 10 && (
              <div className="survey-card fade-in">
                <p className="q-text">Which of these best describes you?</p>
                <div className="option-list">
                  {RESPONDENT_TYPES.map(o => (
                    <button key={o.value} className={`option-btn ${answers.respondentType === o.value ? 'selected' : ''}`} onClick={() => set('respondentType', o.value)}>{o.label}</button>
                  ))}
                </div>
                {step === 10 && answers.respondentType && <button className="btn-primary" onClick={() => { window.scrollTo(0, 0); setPhase('payoff'); }}>See what happened</button>}
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        )}
      </main>

        {phase === 'payoff' && payoff && (
          <div className="payoff-page">
            <div className="survey-card payoff">
              <h2>How your answers compare</h2>
              {payoff.sanctionDistribution ? (
                <>
                  <p className="chart-label">What everyone thought should happen:</p>
                  <PayoffBars data={payoff.sanctionDistribution} yourAnswer={answers.q3a} />
                  <p className="chart-note">Based on {payoff.scenarioResponses} responses to this scenario.</p>
                </>
              ) : (
                /* Below the threshold the API sends null rather than a distribution. Drawing
                   a chart from a handful of people would state a public attitude that a
                   handful of people hold — and until today this chart was rendering all
                   zeros, because no response had ever been recorded. */
                <div className="chart-empty">
                  <p>Not enough responses yet to show how other people answered this one.</p>
                  <p className="chart-note">
                    {payoff.scenarioResponses === 0
                      ? 'You are the first person to answer this scenario.'
                      : `${payoff.scenarioResponses} ${payoff.scenarioResponses === 1 ? 'person has' : 'people have'} answered it so far.`}
                    {' '}We show the comparison once {payoff.minResponsesForChart} people have, so that it means something. Leave your email below and we will tell you what they said.
                  </p>
                </div>
              )}
            </div>

            <div className="survey-card">
              <div className="actual-outcome">
                <span className="outcome-label">What the panel actually decided</span>
                <p className="outcome-text">{payoff.actualOutcome}</p>
                <p className="outcome-desc">{payoff.actualOutcomeDescription}</p>
                <Share />
              </div>
            </div>

            <div className="survey-card">
              <div className="donation-note">
                Your response set aside 50p in our access to justice fund, which supports registrants going through fitness to practise proceedings without representation. &pound;{payoff.totalDonation} of &pound;{payoff.fundCap?.toLocaleString?.() ?? payoff.fundCap} set aside so far, from {payoff.totalResponses} {payoff.totalResponses === 1 ? 'response' : 'responses'}.
              </div>
            </div>

            <div className="survey-card">
              <h3>Thank you</h3>
              <p>Thank you &mdash; that is one more real answer to a question that is usually decided by assumption.</p>
              <p>Get access to more scenarios and see how your views compare across different cases.</p>
              {!emailSubmitted ? (
                <form className="email-form" onSubmit={async (e) => { e.preventDefault(); if (email.includes('@')) { await api.subscribe(email, sessionToken); track('research_email'); setEmailSubmitted(true); } }}>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" required />
                  <button type="submit" className="btn-primary">Submit</button>
                </form>
              ) : (
                <div><p className="highlight">Thank you! You now have access to more scenarios.</p>
                  <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => { setPhase('landing'); startSurvey(); }}>Try another scenario</button></div>
              )}
              <p className="email-privacy">We will never share your email. You can unsubscribe at any time.</p>
            </div>
          </div>
        )}

      <footer className="survey-footer">
        Sequentus Research &copy; {new Date().getFullYear()} &middot; A Sequentus Intelligence study &middot; <a href="https://education.sequentus.org/research/">About this research</a> &middot; <a href="https://education.sequentus.org/privacy-policy/">Privacy</a>
      </footer>
    </div>
  );
}

function FundMeter({ stats }: { stats: any }) {
  const cap = Number(stats.fundCap) || 0;
  const raised = Number(stats.totalDonation) || 0;
  // A real figure, however small. The bar keeps a sliver so it reads as "started", not
  // "broken" — but the number beside it is never rounded up to flatter it.
  const pct = cap > 0 ? Math.min(100, (raised / cap) * 100) : 0;
  return (
    <div className="fund-meter" title={`${stats.totalResponses} responses so far`}>
      <div className="fund-meter__row">
        <span className="fund-meter__figure">&pound;{stats.totalDonation}</span>
        <span className="fund-meter__of">of &pound;{cap.toLocaleString()} set aside</span>
      </div>
      <div className="fund-meter__track">
        <div className="fund-meter__fill" style={{ width: `${Math.max(pct, 1.5)}%` }} />
      </div>
    </div>
  );
}

function PayoffBars({ data, yourAnswer }: { data: Record<string, number>; yourAnswer: string }) {
  const total = Object.values(data || {}).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="results-bars">
      {SANCTION_OPTIONS.map(opt => {
        const count = (data || {})[opt.value] || 0;
        const pct = Math.round((count / total) * 100);
        const isYours = yourAnswer === opt.value;
        return (
          <div key={opt.value} className="result-bar">
            <div className="bar-label">{SANCTION_LABELS[opt.value]} {isYours && '\u2190 you'}</div>
            <div className="bar-track">
              <div className={`bar-fill ${isYours ? 'yours' : ''}`} style={{ width: `${Math.max(pct, 2)}%` }}>{pct > 8 ? `${pct}%` : ''}</div>
            </div>
            <div className="bar-count">{count}</div>
          </div>
        );
      })}
    </div>
  );
}

export default App;
